import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import CustomerLayout from "../CustomerLayout";

const STORAGE_KEY = "hagglefreight_customer_notification_settings_v1";

const defaultSettings = {
  shipmentUpdates: true,
  driverMessages: true,
  paymentNotifications: true,
  promotionsAndDiscounts: false,
  emailAlerts: true,
  pushAlerts: true,
};

const CustomerNotifications = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");

      if (stored && typeof stored === "object") {
        setSettings({
          ...defaultSettings,
          ...stored,
        });
      }
    } catch (error) {
      console.error("Unable to load notification settings:", error);
    }
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleToggle = (name) => (event) => {
    setSettings((current) => ({
      ...current,
      [name]: event.target.checked,
    }));
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    setOpen(false);
    setSaved(true);
  };

  return (
    <CustomerLayout>
      <Box sx={{ maxWidth: 980, mx: "auto", py: { xs: 2, md: 4 } }}>
        <Box sx={{ textAlign: "center", mb: 4, px: { xs: 1, sm: 0 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              letterSpacing: "-0.02em",
              mb: 1,
            }}
          >
            Notifications
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "1rem",
              lineHeight: 1.6,
              maxWidth: 720,
              mx: "auto",
            }}
          >
            Manage load activity, payment notices, and account updates.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: 3,
            p: { xs: 2.5, md: 4 },
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
          }}
        >
          <Box
  sx={{
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      sm: "repeat(3, minmax(0, 1fr))",
    },
    gap: 2,
    width: "100%",
    maxWidth: { xs: 320, sm: 680 },
    mx: "auto",
  }}
>
  <Button
    variant="contained"
    size="large"
    onClick={handleOpen}
    sx={{
      py: 1.25,
      width: "100%",
      textTransform: "none",
      fontWeight: 700,
    }}
  >
    Notifications
  </Button>

  <Button
    variant="outlined"
    size="large"
    onClick={handleOpen}
    sx={{
      py: 1.25,
      width: "100%",
      textTransform: "none",
      fontWeight: 700,
    }}
  >
    Email Alerts
  </Button>

  <Button
    variant="outlined"
    size="large"
    onClick={handleOpen}
    sx={{
      py: 1.25,
      width: "100%",
      textTransform: "none",
      fontWeight: 700,
    }}
  >
    Push Alerts
  </Button>
</Box>
        </Paper>

        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>Notification Settings</DialogTitle>

          <DialogContent dividers>
            <Box sx={{ display: "grid", gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.shipmentUpdates}
                    onChange={handleToggle("shipmentUpdates")}
                  />
                }
                label="Shipment Updates"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.driverMessages}
                    onChange={handleToggle("driverMessages")}
                  />
                }
                label="Driver Messages"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.paymentNotifications}
                    onChange={handleToggle("paymentNotifications")}
                  />
                }
                label="Payment Notifications"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.promotionsAndDiscounts}
                    onChange={handleToggle("promotionsAndDiscounts")}
                  />
                }
                label="Promotions & Discounts"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailAlerts}
                    onChange={handleToggle("emailAlerts")}
                  />
                }
                label="Email Alerts"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.pushAlerts}
                    onChange={handleToggle("pushAlerts")}
                  />
                }
                label="Push Alerts"
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose}>Close</Button>

            <Button variant="contained" onClick={handleSave}>
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={saved}
          autoHideDuration={3200}
          onClose={() => setSaved(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setSaved(false)}
          >
            Notification settings saved.
          </Alert>
        </Snackbar>
      </Box>
    </CustomerLayout>
  );
};

export default CustomerNotifications;